import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { ChevronLeft, Plus, PackageCheck, RotateCw } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { useI18n } from "../lib/i18n.jsx";
import StatusBadge, { PriorityBadge } from "../components/StatusBadge.jsx";
import PartsRequestProgressBar from "../components/PartsRequestProgressBar.jsx";
import CommentThread from "../components/CommentThread.jsx";
import PecExcelImport from "../components/PecExcelImport.jsx";
import QuotationAttachmentPreview from "../components/QuotationAttachmentPreview.jsx";
import SomContextBanner from "../components/SomContextBanner.jsx";
import SomFloatingShortcut from "../components/SomFloatingShortcut.jsx";
import ProgressiveStatusControl from "../components/ProgressiveStatusControl.jsx";
import { fmtDateTime } from "../lib/format.js";

export default function PartsRequestDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useI18n();
  const [pr, setPr] = useState(null);
  const [enums, setEnums] = useState(null);
  const [editAlidata, setEditAlidata] = useState(false);
  const [alidataCo, setAlidataCo] = useState("");
  const [receivingLine, setReceivingLine] = useState(null);
  const [closeReasonDialog, setCloseReasonDialog] = useState(null);

  // Navigation context: when opened from a SOM detail page, the back link
  // returns to that SOM; otherwise it returns to the PEC list.
  const fromSom = location.state?.from === "som" || new URLSearchParams(location.search).get("from") === "som";

  const load = useCallback(async () => {
    const { data } = await api.get(`/parts-requests/${id}`);
    setPr(data);
    setAlidataCo(data.alidata_customer_order || "");
  }, [id]);
  useEffect(() => {
    load();
    api.get("/enums").then((r) => setEnums(r.data));
  }, [id, load]);

  if (!pr || !enums) return <div className="p-6 text-sm text-[#6C757D]">Loading…</div>;

  const saveAlidata = async () => {
    try {
      await api.patch(`/parts-requests/${id}`, { alidata_customer_order: alidataCo, comment: "Alidata CO updated" });
      setEditAlidata(false);
      load();
    } catch (e) {
      alert(formatError(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-3">
        <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
          {fromSom && pr.service_case_id ? (
            <Link
              to={`/service-cases/${pr.service_case_id}`}
              className="flex items-center gap-1 hover:text-[#1F3864]"
              data-testid="pr-back-to-som"
            >
              <ChevronLeft className="w-3 h-3" /> Voltar à obra {pr.service_case_number}
            </Link>
          ) : (
            <Link to="/parts-requests" className="flex items-center gap-1 hover:text-[#1F3864]" data-testid="pr-back-to-list">
              <ChevronLeft className="w-3 h-3" /> Encomenda de Peças
            </Link>
          )}
          <span>/</span>
          <span className="font-mono" data-testid="pr-breadcrumb">{pr.number}{pr.service_case_number ? ` (${pr.service_case_number})` : ""}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-xl font-bold text-[#1A1A2E]" data-testid="pr-title">
                <span className="font-mono">{pr.number}</span>
                {pr.service_case_number && <span className="text-[#6C757D]"> — </span>}
                {pr.service_case_number && <Link to={`/service-cases/${pr.service_case_id}`} className="font-mono text-[#1F3864] hover:underline">{pr.service_case_number}</Link>}
              </h1>
              <PriorityBadge priority={pr.priority} />
              <StatusBadge status={pr.current_status} />
            </div>
            {pr.pcp_number && (
              <div className="mt-1 text-xs text-[#6C757D]" data-testid="pr-from-pcp">
                Originada do <Link to="/classification-requests" className="text-[#1F3864] hover:underline font-mono font-semibold">{pr.pcp_number}</Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {pr.service_case_id && <SomContextBanner caseId={pr.service_case_id} testid="pr-som-context" />}
        <PartsRequestProgressBar status={pr.current_status} />

        <ProgressiveStatusControl
          entity="pec"
          recordId={id}
          currentStatus={pr.current_status}
          onAdvance={async (newStatus, comment, opts) => {
            const body = { new_status: newStatus, comment };
            if (opts?.appointment_date) body.appointment_date = opts.appointment_date;
            try {
              await api.post(`/parts-requests/${id}/status`, body);
              await load();
            } catch (e) {
              // Iter 63 · Fix — se o backend exige `close_reason`
              // (fecho de PEC com SOM ainda a aguardar peças), abrimos o
              // diálogo especializado. Só entramos aqui quando a resposta
              // é 400 e a mensagem indica que o motivo é obrigatório.
              const detail = e?.response?.data?.detail || "";
              const needsCloseReason = e?.response?.status === 400 &&
                typeof detail === "string" &&
                detail.toLowerCase().includes("motivo de encerramento");
              if (needsCloseReason) {
                setCloseReasonDialog({ newStatus, comment });
              } else {
                throw e;
              }
            }
          }}
        />
        {closeReasonDialog && (
          <PecCloseReasonDialog
            pec={pr}
            initial={closeReasonDialog}
            onClose={() => setCloseReasonDialog(null)}
            onSubmit={async ({ close_reason, close_reason_note }) => {
              await api.post(`/parts-requests/${id}/status`, {
                new_status: closeReasonDialog.newStatus,
                comment: closeReasonDialog.comment || `Encerramento com motivo: ${close_reason}`,
                close_reason,
                close_reason_note: close_reason_note || undefined,
              });
              setCloseReasonDialog(null);
              await load();
            }}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {pr.quotation_attachment_id && (
            <div className="bg-white border border-gray-200 rounded-md p-4">
              <QuotationAttachmentPreview
                attachmentId={pr.quotation_attachment_id}
                title="Orçamento anexado"
                testidPrefix="pr-quotation-preview"
              />
            </div>
          )}
          <div className="bg-white border border-gray-200 rounded-md p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E]">Parts lines ({pr.lines.length})</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <ReorderCancelledButton prId={id} lines={pr.lines} onAdded={load} />
                <AddLineButton prId={id} onAdded={load} />
              </div>
            </div>
            <div className="mb-3">
              <PecExcelImport pec={pr} onImported={load} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Part #</th>
                    <th className="px-2 py-1.5 text-left">Description</th>
                    <th className="px-2 py-1.5 text-left">Qty</th>
                    <th className="px-2 py-1.5 text-left">Recv</th>
                    <th className="px-2 py-1.5 text-left">Pending</th>
                    <th className="px-2 py-1.5 text-left">Source</th>
                    <th className="px-2 py-1.5 text-left">Status</th>
                    <th className="px-2 py-1.5 text-left">ETA</th>
                    <th className="px-2 py-1.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pr.lines.length === 0 ? (
                    <tr><td colSpan={9} className="px-2 py-4 text-center text-[#6C757D]">No parts lines yet</td></tr>
                  ) : pr.lines.map((l) => (
                    <PartsLineRow key={l.id} prId={id} line={l} enums={enums} onChange={load} onMarkReceived={setReceivingLine} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-md p-4">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-2">Notes</h3>
            <p className="text-sm whitespace-pre-wrap text-[#1A1A2E]">{pr.notes || "—"}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-md p-4">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-2">Alidata customer order</h3>
            {editAlidata ? (
              <div className="flex gap-2">
                <input value={alidataCo} onChange={(e) => setAlidataCo(e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-300 rounded-sm text-sm font-mono" data-testid="pr-alidata-input" />
                <button onClick={saveAlidata} className="text-xs bg-[#1F3864] text-white px-2 rounded-sm" data-testid="pr-alidata-save">Save</button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className={`text-sm font-mono ${pr.alidata_customer_order ? "text-[#1A1A2E]" : "text-[#DC3545]"}`}>
                  {pr.alidata_customer_order || "Missing"}
                </span>
                <button onClick={() => setEditAlidata(true)} className="text-xs text-[#1F3864] hover:underline" data-testid="pr-alidata-edit">Edit</button>
              </div>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-md p-4">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-2">ETA mais próximo</h3>
            {(() => {
              if (!pr.overall_eta) return <div className="text-sm text-[#6C757D]">—</div>;
              const overdue = new Date(pr.overall_eta) < new Date();
              return (
                <div className={`text-sm font-medium ${overdue ? "text-[#DC3545]" : "text-[#1A1A2E]"}`} data-testid="pr-overall-eta">
                  {fmtDateTime(pr.overall_eta)}
                  {overdue && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#DC3545] text-white px-1.5 py-0.5 rounded-sm">Vencido</span>}
                </div>
              );
            })()}
          </div>
        </div>
        </div>
      </div>

      {receivingLine && (
        <MarkReceivedDialog
          prId={id}
          line={receivingLine}
          onClose={() => setReceivingLine(null)}
          onSaved={() => {
            setReceivingLine(null);
            load();
          }}
        />
      )}

      <div className="mt-4">
        <CommentThread recordType="parts_request" recordId={id} />
      </div>

      {pr.service_case_id && <SomFloatingShortcut caseId={pr.service_case_id} currentModule="pec" />}
    </div>
  );
}

function MarkReceivedDialog({ prId, line, onClose, onSaved }) {
  const ordered = line.quantity_ordered || 0;
  const alreadyReceived = line.quantity_received || 0;
  const remaining = Math.max(0, ordered - alreadyReceived);
  // Default: receive everything still outstanding
  const [units, setUnits] = useState(remaining);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const numericUnits = Number(units) || 0;
  const newTotal = alreadyReceived + numericUnits;
  const newPending = Math.max(0, ordered - newTotal);
  const willComplete = newTotal >= ordered;
  const projectedStatus = willComplete ? "Received complete" : newTotal > 0 ? "Received partial" : line.line_status;

  const submit = async () => {
    setError("");
    if (numericUnits <= 0) {
      setError("Units received must be greater than 0.");
      return;
    }
    if (numericUnits > remaining) {
      setError(`Only ${remaining} unit${remaining === 1 ? "" : "s"} outstanding.`);
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/parts-requests/${prId}/lines/${line.id}`, {
        quantity_received: newTotal,
        quantity_ordered: ordered,
        comment: notes
          ? `Marked ${numericUnits} unit(s) received. ${notes}`
          : `Marked ${numericUnits} unit(s) received.`,
      });
      onSaved();
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="mark-received-dialog">
        <div className="px-5 py-3 border-b border-gray-200 bg-[#F4F6F8]">
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Mark units received</div>
          <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">{line.part_number}</h2>
          <div className="text-xs text-[#6C757D] mt-0.5 truncate">{line.description}</div>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Mini label="Ordered" value={ordered} />
            <Mini label="Already received" value={alreadyReceived} accent="green" />
            <Mini label="Outstanding" value={remaining} accent={remaining > 0 ? "amber" : "grey"} />
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">How many units were received? *</span>
            <input
              type="number"
              min={1}
              max={remaining}
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              autoFocus
              className="w-full mt-0.5 px-2 py-2 border border-gray-300 rounded-sm text-lg font-mono font-bold text-[#1A1A2E] focus:outline-none focus:ring-2 focus:ring-[#28A745]/40 focus:border-[#28A745]"
              data-testid="mark-received-units"
            />
            <div className="text-[11px] text-[#6C757D] mt-0.5">Max {remaining} (already received {alreadyReceived} of {ordered})</div>
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Notes (optional)</span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. arrived in good condition"
              className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
              data-testid="mark-received-notes"
            />
          </label>

          <div className="bg-[#F4F6F8] border border-gray-200 rounded-sm p-3 text-xs">
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1.5">After this update</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className="text-[10px] text-[#6C757D]">Total received</div>
                <div className="font-mono font-bold text-[#28A745]" data-testid="mark-received-projected-total">{newTotal}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#6C757D]">Pending</div>
                <div className="font-mono font-bold text-[#1A1A2E]" data-testid="mark-received-projected-pending">{newPending}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#6C757D]">Line status</div>
                <div className="font-semibold text-[#1A1A2E]" data-testid="mark-received-projected-status">{projectedStatus}</div>
              </div>
            </div>
            {willComplete && (
              <div className="mt-2 text-[11px] text-[#155724] bg-[#D4EDDA] border border-[#28A745]/30 px-2 py-1 rounded-sm">
                This will complete the line. If all lines are complete, the Parts Request becomes <b>Fully received</b> and the linked Service Case moves to <b>Parts available</b>.
              </div>
            )}
          </div>

          {error && (
            <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="mark-received-error">
              {error}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || numericUnits <= 0 || numericUnits > remaining}
            className="px-4 py-2 text-sm bg-[#28A745] hover:bg-[#1f7a32] text-white rounded-sm disabled:opacity-60 font-medium"
            data-testid="mark-received-submit"
          >
            {submitting ? "Saving…" : `Confirm ${numericUnits || 0} received`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, accent }) {
  const color = accent === "green" ? "text-[#28A745]" : accent === "amber" ? "text-[#B45309]" : "text-[#1A1A2E]";
  return (
    <div className="bg-white border border-gray-200 rounded-sm px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className={`font-heading font-bold text-lg ${color}`}>{value}</div>
    </div>
  );
}

function PartsLineRow({ prId, line, enums, onChange, onMarkReceived }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...line, eta: line.eta ? line.eta.slice(0, 16) : "" });
  const save = async () => {
    try {
      await api.patch(`/parts-requests/${prId}/lines/${line.id}`, {
        ...form,
        eta: form.eta ? new Date(form.eta).toISOString() : null,
        quantity_received: parseInt(form.quantity_received || 0, 10),
        quantity_ordered: parseInt(form.quantity_ordered || 0, 10),
        comment: "Line updated",
      });
      setEditing(false);
      onChange();
    } catch (e) {
      alert(formatError(e.response?.data?.detail));
    }
  };

  if (!editing) {
    const ordered = line.quantity_ordered || 0;
    const received = line.quantity_received || 0;
    const pending = Math.max(0, ordered - received);
    const isReceiveable = !["Received complete", "Cancelled", "Replaced"].includes(line.line_status);
    return (
      <tr className="border-t border-gray-100 hover:bg-[#F8F9FA]" data-testid={`line-${line.id}`}>
        <td className="px-2 py-1.5 font-mono text-xs">{line.part_number}</td>
        <td className="px-2 py-1.5">{line.description}</td>
        <td className="px-2 py-1.5 text-xs">{ordered}</td>
        <td className="px-2 py-1.5 text-xs" data-testid={`line-received-${line.id}`}>
          <span className={received > 0 ? "font-semibold text-[#28A745]" : ""}>{received}</span>
        </td>
        <td className="px-2 py-1.5 text-xs" data-testid={`line-pending-${line.id}`}>
          <span className={pending > 0 ? "text-[#1A1A2E]" : "text-[#28A745] font-semibold"}>{pending}</span>
        </td>
        <td className="px-2 py-1.5 text-xs">{line.source}</td>
        <td className="px-2 py-1.5" data-testid={`line-status-${line.id}`}><StatusBadge status={line.line_status} /></td>
        <td className="px-2 py-1.5 text-xs">{line.eta ? fmtDateTime(line.eta) : "—"}</td>
        <td className="px-2 py-1.5 text-xs whitespace-nowrap">
          {isReceiveable && (
            <button
              onClick={() => onMarkReceived(line)}
              className="inline-flex items-center gap-1 text-[#28A745] hover:underline mr-3 font-medium"
              data-testid={`line-receive-${line.id}`}
            >
              <PackageCheck className="w-3 h-3" /> Mark received
            </button>
          )}
          <button onClick={() => setEditing(true)} className="text-[#1F3864] hover:underline" data-testid={`line-edit-${line.id}`}>Edit</button>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-t border-gray-100 bg-[#F4F6F8]">
      <td className="px-2 py-1.5"><input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} className="w-full text-xs px-1 py-0.5 border border-gray-300 rounded-sm" /></td>
      <td className="px-2 py-1.5"><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full text-xs px-1 py-0.5 border border-gray-300 rounded-sm" /></td>
      <td className="px-2 py-1.5"><input type="number" value={form.quantity_ordered} onChange={(e) => setForm({ ...form, quantity_ordered: e.target.value })} className="w-14 text-xs px-1 py-0.5 border border-gray-300 rounded-sm" /></td>
      <td className="px-2 py-1.5"><input type="number" value={form.quantity_received || 0} onChange={(e) => setForm({ ...form, quantity_received: e.target.value })} className="w-14 text-xs px-1 py-0.5 border border-gray-300 rounded-sm" data-testid={`line-qty-${line.id}`} /></td>
      <td className="px-2 py-1.5 text-xs text-[#6C757D]">{Math.max(0, (parseInt(form.quantity_ordered, 10) || 0) - (parseInt(form.quantity_received, 10) || 0))}</td>
      <td className="px-2 py-1.5">
        <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="text-xs px-1 py-0.5 border border-gray-300 rounded-sm">
          {["Local stock", "Factory", "Supplier", "Backorder"].map((s) => <option key={s}>{s}</option>)}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <select value={form.line_status} onChange={(e) => setForm({ ...form, line_status: e.target.value })} className="text-xs px-1 py-0.5 border border-gray-300 rounded-sm">
          {enums.parts_line_statuses.map((s) => <option key={s}>{s}</option>)}
        </select>
      </td>
      <td className="px-2 py-1.5"><input type="datetime-local" value={form.eta || ""} onChange={(e) => setForm({ ...form, eta: e.target.value })} className="text-xs px-1 py-0.5 border border-gray-300 rounded-sm" /></td>
      <td className="px-2 py-1.5 text-xs">
        <button onClick={save} className="text-[#28A745] font-semibold mr-2" data-testid={`line-save-${line.id}`}>Save</button>
        <button onClick={() => setEditing(false)} className="text-[#6C757D]">Cancel</button>
      </td>
    </tr>
  );
}

function AddLineButton({ prId, onAdded }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ part_number: "", description: "", quantity_ordered: 1, source: "Factory", line_status: "Requested", eta: "" });
  const submit = async () => {
    try {
      await api.post(`/parts-requests/${prId}/lines`, {
        ...form,
        quantity_ordered: parseInt(form.quantity_ordered, 10),
        eta: form.eta ? new Date(form.eta).toISOString() : null,
      });
      setShow(false);
      setForm({ part_number: "", description: "", quantity_ordered: 1, source: "Factory", line_status: "Requested", eta: "" });
      onAdded();
    } catch (e) {
      alert(formatError(e.response?.data?.detail));
    }
  };
  return (
    <>
      <button onClick={() => setShow(true)} className="text-xs bg-[#1F3864] text-white px-2.5 py-1.5 rounded-sm hover:bg-[#142542] flex items-center gap-1" data-testid="add-line-btn">
        <Plus className="w-3 h-3" /> Add line
      </button>
      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShow(false)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200"><h2 className="font-heading text-lg font-bold">Add parts line</h2></div>
            <div className="p-5 space-y-3">
              <Inp label="Part number *" v={form.part_number} on={(v) => setForm({ ...form, part_number: v })} testid="line-pn" />
              <Inp label="Description *" v={form.description} on={(v) => setForm({ ...form, description: v })} testid="line-desc" />
              <Inp label="Quantity *" type="number" v={form.quantity_ordered} on={(v) => setForm({ ...form, quantity_ordered: v })} />
              <Inp label="ETA" type="datetime-local" v={form.eta} on={(v) => setForm({ ...form, eta: v })} />
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Source</span>
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm">
                  {["Local stock", "Factory", "Supplier", "Backorder"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm">Cancel</button>
              <button onClick={submit} className="px-4 py-2 text-sm bg-[#1F3864] text-white rounded-sm" data-testid="line-submit">Add</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Inp({ label, v, on, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm" data-testid={testid} />
    </label>
  );
}

function ReorderCancelledButton({ prId, lines, onAdded }) {
  const cancelled = lines.filter((l) => l.line_status === "Cancelada");
  const [show, setShow] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (cancelled.length === 0) return null;

  const open = () => {
    setRows(
      cancelled.map((l) => ({
        source_line_id: l.id,
        checked: true,
        part_number: l.part_number,
        description: l.description,
        quantity_ordered: Math.max(1, (l.quantity_ordered || 0) - (l.quantity_received || 0)),
        source: l.source || "Factory",
        eta: "",
        notes: `Reorder of cancelled line ${l.part_number}`,
      }))
    );
    setErr("");
    setShow(true);
  };

  const update = (idx, patch) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const submit = async () => {
    setErr("");
    const selected = rows.filter((r) => r.checked);
    if (selected.length === 0) {
      setErr("Select at least one line to reorder.");
      return;
    }
    for (const r of selected) {
      if (!r.part_number.trim() || !r.description.trim() || Number(r.quantity_ordered) <= 0) {
        setErr("Each selected row needs part number, description and quantity > 0.");
        return;
      }
    }
    setBusy(true);
    try {
      for (const r of selected) {
        await api.post(`/parts-requests/${prId}/lines`, {
          part_number: r.part_number.trim(),
          description: r.description.trim(),
          quantity_ordered: Number(r.quantity_ordered),
          source: r.source,
          line_status: "Requested",
          eta: r.eta ? new Date(r.eta).toISOString() : null,
          notes: r.notes || null,
        });
      }
      setShow(false);
      onAdded();
    } catch (e) {
      setErr(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={open}
        className="text-xs bg-white border border-[#B45309] text-[#B45309] hover:bg-[#FFF8EC] px-2.5 py-1.5 rounded-sm font-medium inline-flex items-center gap-1"
        data-testid="reorder-cancelled-btn"
        title="Re-order parts that were cancelled (e.g. supplier suggested an equivalent)"
      >
        <RotateCw className="w-3 h-3" /> Reorder cancelled ({cancelled.length})
      </button>
      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShow(false)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reorder-cancelled-dialog">
            <div className="px-5 py-3 border-b border-gray-200">
              <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Reorder cancelled parts</div>
              <h2 className="font-heading text-base font-bold text-[#1A1A2E]">{cancelled.length} cancelled line{cancelled.length === 1 ? "" : "s"} ready to reorder</h2>
              <p className="text-xs text-[#6C757D] mt-0.5">Edit part number / description if the supplier suggested an equivalent. Uncheck rows you don&rsquo;t want to recreate.</p>
            </div>
            <div className="p-4 space-y-2">
              {rows.map((r, i) => (
                <div key={r.source_line_id} className="grid grid-cols-12 gap-2 items-end bg-[#F8FAFC] p-2 rounded-sm" data-testid={`reorder-row-${i}`}>
                  <label className="col-span-1 flex items-center justify-center pt-3">
                    <input
                      type="checkbox"
                      checked={r.checked}
                      onChange={(e) => update(i, { checked: e.target.checked })}
                      data-testid={`reorder-row-${i}-check`}
                    />
                  </label>
                  <input
                    placeholder="Part #"
                    value={r.part_number}
                    onChange={(e) => update(i, { part_number: e.target.value })}
                    disabled={!r.checked}
                    className={`col-span-2 mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-xs font-mono disabled:bg-gray-100`}
                    data-testid={`reorder-row-${i}-pn`}
                  />
                  <input
                    placeholder="Description"
                    value={r.description}
                    onChange={(e) => update(i, { description: e.target.value })}
                    disabled={!r.checked}
                    className={`col-span-4 mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm disabled:bg-gray-100`}
                    data-testid={`reorder-row-${i}-desc`}
                  />
                  <input
                    type="number"
                    min="1"
                    value={r.quantity_ordered}
                    onChange={(e) => update(i, { quantity_ordered: e.target.value })}
                    disabled={!r.checked}
                    className="col-span-1 mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm disabled:bg-gray-100"
                    data-testid={`reorder-row-${i}-qty`}
                  />
                  <select
                    value={r.source}
                    onChange={(e) => update(i, { source: e.target.value })}
                    disabled={!r.checked}
                    className="col-span-2 mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm disabled:bg-gray-100"
                  >
                    {["Local stock", "Factory", "Supplier", "Backorder"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <input
                    type="date"
                    value={r.eta}
                    onChange={(e) => update(i, { eta: e.target.value })}
                    disabled={!r.checked}
                    className="col-span-2 mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm disabled:bg-gray-100"
                  />
                </div>
              ))}
              {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="reorder-error">{err}</div>}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]" disabled={busy}>Cancel</button>
              <button
                onClick={submit}
                disabled={busy}
                className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-50 font-medium"
                data-testid="reorder-submit"
              >
                {busy ? "Creating…" : `Create ${rows.filter((r) => r.checked).length} new line${rows.filter((r) => r.checked).length === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// -----------------------------------------------------------------------------
// Iter 63 · Fix — diálogo para escolher `close_reason` quando um PEC é
// encerrado / cancelado com a SOM ainda a aguardar peças. O backend só exige
// este campo neste cenário (validado por 400); UI não impõe carga cognitiva
// em fechos "normais" (ex: já passou por Recebido completo).
// -----------------------------------------------------------------------------
function PecCloseReasonDialog({ pec, initial, onClose, onSubmit }) {
  const [reason, setReason] = React.useState("Peças recebidas e entregues");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const OPTIONS = [
    {
      value: "Peças recebidas e entregues",
      hint: "As peças chegaram e foram entregues ao cliente/oficina. A SOM avança para 'Peças disponíveis'.",
    },
    {
      value: "Reparação cancelada",
      hint: "O cliente ou a oficina cancelou a reparação. A SOM NÃO avança — é criada uma tarefa para o SA.",
    },
    {
      value: "Substituído por garantia",
      hint: "As peças foram tratadas via processo de garantia (SWC). A SOM NÃO avança — é criada uma tarefa para o SA.",
    },
    {
      value: "Outro",
      hint: "Descreve o motivo no campo abaixo. A SOM NÃO avança — é criada uma tarefa para o SA.",
    },
  ];

  const submit = async () => {
    setErr("");
    if (reason === "Outro" && !note.trim()) {
      setErr("Explica o motivo no campo abaixo.");
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ close_reason: reason, close_reason_note: note.trim() });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Falha ao encerrar o PEC.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="pec-close-reason-dialog"
      >
        <div className="px-5 py-3 border-b border-gray-200 bg-[#F4F6F8]">
          <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">
            Motivo do encerramento — {pec.number}
          </h2>
          <div className="text-xs text-[#6C757D] mt-1">
            A SOM linkada ainda espera peças. Indica o que aconteceu antes de fechar.
          </div>
        </div>
        <div className="p-5 space-y-2">
          {OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`block border rounded-sm p-2.5 cursor-pointer ${
                reason === opt.value ? "border-[#1F3864] bg-[#F8FAFC]" : "border-gray-200"
              }`}
              data-testid={`pec-close-reason-option-${opt.value.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="close_reason"
                  value={opt.value}
                  checked={reason === opt.value}
                  onChange={() => setReason(opt.value)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[#1A1A2E]">{opt.value}</div>
                  <div className="text-xs text-[#6C757D] mt-0.5">{opt.hint}</div>
                </div>
              </div>
            </label>
          ))}
          {reason === "Outro" && (
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Explica o motivo…"
              className="w-full mt-2 px-2 py-1.5 border border-gray-300 rounded-sm text-sm h-20 focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
              data-testid="pec-close-reason-note"
            />
          )}
          {err && (
            <div
              className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm"
              data-testid="pec-close-reason-error"
            >
              {err}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8] disabled:opacity-60"
            data-testid="pec-close-reason-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60"
            data-testid="pec-close-reason-submit"
          >
            {busy ? "A encerrar…" : "Confirmar encerramento"}
          </button>
        </div>
      </div>
    </div>
  );
}
